import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Component, Suspense, lazy, useEffect, useState } from "react";
import type { ReactNode, ErrorInfo } from "react";
import { useAuth } from "./lib/auth";
import { useTelegram, telegramAuth } from "./lib/telegram";
import { useTelegramBackButton } from "./hooks/useTelegramBackButton";

const IndexPage          = lazy(() => import("./pages/IndexPage"));
const LoginPage          = lazy(() => import("./pages/LoginPage"));
const DashboardPage      = lazy(() => import("./pages/DashboardPage"));
const CoordinatorPage    = lazy(() => import("./pages/CoordinatorPage"));
const WorkerDashboardPage = lazy(() => import("./pages/WorkerDashboardPage"));
const UsersPage          = lazy(() => import("./pages/UsersPage"));
const NotFoundPage       = lazy(() => import("./pages/NotFoundPage"));

function LoadingFallback() {
  return (
    <div style={{
      display: "flex", height: "100vh",
      alignItems: "center", justifyContent: "center",
      background: "rgb(var(--bg))", color: "rgb(var(--fg2))",
      fontFamily: "IBM Plex Mono, monospace", fontSize: 11,
    }}>Yuklanmoqda…</div>
  );
}

class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null };
  static getDerivedStateFromError(e: Error) { return { error: e }; }
  componentDidCatch(_e: Error, _info: ErrorInfo) { /* could log to Sentry */ }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-slate-950 text-white font-mono">
          <div className="text-4xl">⚠️</div>
          <div className="text-sm font-semibold">Xato yuz berdi</div>
          <div className="text-xs text-slate-400 max-w-md text-center">{(this.state.error as Error).message}</div>
          <button onClick={() => window.location.reload()}
            className="px-4 py-2 rounded-lg border border-slate-700 text-slate-300 hover:text-white text-xs transition-colors">
            Qayta yuklash
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function RequireHokim({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  const user = useAuth.getState().user;
  if (user && user.role !== "hokim") return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function RequireCoordinator({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  const user = useAuth.getState().user;
  if (user && !["hokim", "hokimiyat_xodimi"].includes(user.role)) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function RequireWorker({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  const user = useAuth.getState().user;
  if (user && user.role !== "nazar_worker") return <Navigate to="/login" replace />;
  return <>{children}</>;
}

const ROLE_ROUTE: Record<string, string> = {
  hokim: "/dashboard",
  hokimiyat_xodimi: "/coordinator",
  nazar_worker: "/worker",
};

function TelegramAutoLogin({ children }: { children: ReactNode }) {
  const { isTelegram } = useTelegram();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [ready, setReady] = useState(!isTelegram);
  useTelegramBackButton();

  useEffect(() => {
    if (!isTelegram || isAuthenticated()) {
      setReady(true);
      return;
    }
    telegramAuth()
      .then((data) => {
        localStorage.setItem("yrbp-access-token", data.access);
        localStorage.setItem("yrbp-refresh-token", data.refresh);
        localStorage.setItem("yrbp-user", JSON.stringify(data.user));
        useAuth.setState({ access: data.access, refresh: data.refresh, user: data.user });
        navigate(ROLE_ROUTE[data.user.role] ?? "/", { replace: true });
      })
      .catch(() => {})
      .finally(() => setReady(true));
  }, [isTelegram]);

  if (!ready) return <LoadingFallback />;
  return <>{children}</>;
}

export default function App() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingFallback />}>
        <TelegramAutoLogin>
          <Routes>
            <Route path="/"            element={<IndexPage />} />
            <Route path="/login"       element={<LoginPage />} />
            <Route path="/dashboard"   element={<RequireHokim><DashboardPage /></RequireHokim>} />
            <Route path="/coordinator" element={<RequireCoordinator><CoordinatorPage /></RequireCoordinator>} />
            <Route path="/worker"      element={<RequireWorker><WorkerDashboardPage /></RequireWorker>} />
            <Route path="/users"       element={<RequireHokim><UsersPage /></RequireHokim>} />
            <Route path="*"            element={<NotFoundPage />} />
          </Routes>
        </TelegramAutoLogin>
      </Suspense>
    </ErrorBoundary>
  );
}
